<script lang="ts" context="module">
    export const [setContextMenuContext, getContextMenuContext] = createContext<{
        onCallback: () => void
    }>()
</script>

<script lang="ts">
    import { createContext } from "@utils/context-ts"
    import { AddEscapeStack } from "@utils/underhood/escape-key"
    import { createEventDispatcher, onMount, tick } from "svelte"
    import { fade } from "svelte/transition"
    const dispatch = createEventDispatcher()

    setContextMenuContext({
        onCallback: () => {
            exit("item callback")
        },
    })
    type Position = "br" | "bl" | "tr" | "tl"
    type Placement = "absolute" | "fixed"

    let renderPosition: Position = "br"
    let menu: HTMLDivElement | undefined
    let fixedStyle = ""
    $: menuStyle = placement === "fixed" ? fixedStyle || "visibility: hidden;" : ""
    let windowWidth = 0
    let windowHeight = 0
    $: if (menu && windowWidth && windowHeight) {
        getBestPosition().then((position) => {
            renderPosition = position
            fixedStyle = placement === "fixed" ? getFixedStyle(position) : ""
        })
    }

    onMount(() => {
        debug("mounted", { placement, positions })
        const removeEscape = AddEscapeStack({
            name: "ContextMenu",
            fn: () => {
                exit("escape")
            },
        })
        return () => {
            debug("destroyed", { placement })
            removeEscape()
        }
    })

    /**
     * Which positions to check for in list of priority, defaults to first one if none found
     */
    export let positions: Position[] = ["br", "bl", "tr", "tl"]
    export let placement: Placement = "absolute"
    export let variant: "default" | "reader-tabs" = "default"
    let className = ""
    export { className as class }

    // ORDER -> bottom right, bottom left, top right, top right
    async function getBestPosition(): Promise<Position> {
        if (menu === undefined) {
            console.error("Undefined menu!")
            return positions[0]
        }

        if (placement === "fixed") return getBestFixedPosition()

        let maxCoverage = 0
        let bestPosition: Position | null = null

        const elementRect = menu.getBoundingClientRect()

        for (const position of positions) {
            // Apply the configuration to the element
            renderPosition = position
            await tick()

            const rect = menu.getBoundingClientRect()

            const left = Math.max(0, rect.left)
            const right = Math.min(windowWidth, rect.right)
            const top = Math.max(0, rect.top)
            const bottom = Math.min(windowHeight, rect.bottom)

            const width = right - left
            const height = bottom - top

            // Check if the element is outside the window
            if (width < 0 || height < 0) continue

            const coverage = (width * height) / (elementRect.width * elementRect.height)

            if (coverage === 1) {
                return position // Return immediately if coverage is 100%
            }

            if (coverage > maxCoverage) {
                maxCoverage = coverage
                bestPosition = position
            }
        }

        return bestPosition ?? positions[0]
    }

    function getBestFixedPosition(): Position {
        if (menu === undefined) return positions[0]

        let maxCoverage = 0
        let bestPosition: Position | null = null

        const menuRect = menu.getBoundingClientRect()
        const totalArea = menuRect.width * menuRect.height

        for (const position of positions) {
            const rect = getFixedRect(position)
            if (!rect || totalArea <= 0) continue

            const left = Math.max(0, rect.left)
            const right = Math.min(windowWidth, rect.right)
            const top = Math.max(0, rect.top)
            const bottom = Math.min(windowHeight, rect.bottom)

            const width = right - left
            const height = bottom - top

            if (width < 0 || height < 0) continue

            const coverage = (width * height) / totalArea

            if (coverage === 1) return position

            if (coverage > maxCoverage) {
                maxCoverage = coverage
                bestPosition = position
            }
        }

        return bestPosition ?? positions[0]
    }

    function getFixedRect(position: Position) {
        if (!menu) return null

        const anchorRect = menu.parentElement?.getBoundingClientRect()
        if (!anchorRect) return null

        const menuRect = menu.getBoundingClientRect()
        const width = menuRect.width
        const height = menuRect.height
        let left = anchorRect.left
        let top = anchorRect.top

        if (position === "bl" || position === "tl") left = anchorRect.right - width
        if (position === "tr" || position === "tl") top = anchorRect.bottom - height

        return {
            left,
            right: left + width,
            top,
            bottom: top + height,
        }
    }

    function getFixedStyle(position: Position) {
        const rect = getFixedRect(position)
        if (!rect) return ""

        return `left: ${rect.left}px; top: ${rect.top}px;`
    }

    // Little hack so the first click doesn't count
    // as a clickoutside
    let ready = false
    setTimeout(() => (ready = true), 100)

    function debug(message: string, details?: Record<string, unknown>) {
        if (!import.meta.env.DEV) return
        console.info("[context-menu]", message, details ?? {})
    }

    function exit(reason: string, event?: Event) {
        debug("exit", {
            reason,
            target:
                event?.target instanceof HTMLElement
                    ? event.target.outerHTML.slice(0, 120)
                    : undefined,
        })
        dispatch("exit")
    }

    function potentialClickOutside(e: MouseEvent) {
        if (!menu || !e.target || !ready) return
        if (!menu.contains(<Node>e.target) && !e.defaultPrevented) {
            exit("outside click", e)
        }
    }
</script>

<svelte:window
    on:click={potentialClickOutside}
    bind:innerHeight={windowHeight}
    bind:innerWidth={windowWidth}
/>
<div
    role="menu"
    tabindex="0"
    transition:fade={{ duration: 200 }}
    on:click={(e) => e.stopPropagation()}
    bind:this={menu}
    class={`context-menu ${className}`}
    class:absolute-menu={placement === "absolute"}
    class:fixed-menu={placement === "fixed"}
    class:reader-tabs-menu={variant === "reader-tabs"}
    style={menuStyle}
    data-placement={placement}
    data-position={renderPosition}
    data-variant={variant}
    {...$$restProps}
>
    <slot />
</div>

<style>
    .context-menu {
        --context-menu-surface: var(--color-grey-0);
        --context-menu-border: var(--color-purple-100);
        --context-menu-text: var(--color-grey-900);
        --context-menu-item-bg: var(--color-grey-0);
        --context-menu-item-separator: var(--color-purple-100);
        --context-menu-item-text: var(--color-purple-700);
        --context-menu-item-icon: var(--color-purple-600);
        --context-menu-item-hover-bg: var(--color-purple-50);
        --context-menu-item-hover-separator: var(--color-purple-100);
        --context-menu-item-hover-text: var(--color-purple-800);
        --context-menu-item-active-bg: var(--color-purple-100);
        --context-menu-item-active-separator: var(--color-purple-100);
        --context-menu-item-active-text: var(--color-purple-800);
        --context-menu-focus-outline: var(--color-gold-400);
        --context-menu-danger-bg: var(--context-menu-item-bg);
        --context-menu-danger-separator: color-mix(
            in srgb,
            var(--danger) 24%,
            var(--context-menu-item-separator)
        );
        --context-menu-danger-text: var(--danger);
        --context-menu-danger-hover-bg: color-mix(
            in srgb,
            var(--danger) 10%,
            var(--context-menu-item-bg)
        );
        --context-menu-danger-hover-separator: var(--context-menu-danger-separator);
        --context-menu-danger-active-bg: color-mix(
            in srgb,
            var(--danger) 14%,
            var(--context-menu-item-bg)
        );
        --context-menu-danger-active-separator: var(--context-menu-danger-separator);
        --context-menu-radius: 0.85rem;

        display: grid;
        z-index: 10;
        width: max-content;
        min-width: min(11rem, calc(100vw - 2rem));
        max-width: calc(
            100vw - max(1rem, var(--safe-area-inset-left, 0rem)) -
                max(1rem, var(--safe-area-inset-right, 0rem))
        );
        max-height: calc(
            100dvh - max(1rem, var(--safe-area-inset-top, 0rem)) -
                max(1rem, var(--safe-area-inset-bottom, 0rem))
        );
        grid-template-columns: minmax(0, 1fr);
        align-items: stretch;
        gap: 0;
        overflow: hidden auto;
        border: 1px solid var(--context-menu-border);
        border-radius: var(--context-menu-radius);
        background: var(--context-menu-surface);
        box-shadow: none;
        color: var(--context-menu-text);
        font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        font-size: 0.96rem;
        padding: 0;
    }

    .context-menu.reader-tabs-menu {
        --context-menu-surface: var(--reader-tabs-tab-bg, var(--color-grey-0));
        --context-menu-border: var(--reader-tabs-tab-border, var(--color-purple-100));
        --context-menu-text: var(--reader-tabs-strong, var(--color-grey-900));
        --context-menu-item-bg: var(--reader-tabs-tab-bg, var(--color-grey-0));
        --context-menu-item-separator: var(--reader-tabs-tab-border, var(--color-purple-100));
        --context-menu-item-text: var(--reader-tabs-strong, var(--color-grey-900));
        --context-menu-item-icon: var(--reader-tabs-muted, var(--color-grey-500));
        --context-menu-item-hover-bg: var(--reader-tabs-hover-bg, var(--color-purple-50));
        --context-menu-item-hover-separator: var(
            --reader-tabs-tab-hover-border,
            var(--color-purple-100)
        );
        --context-menu-item-hover-text: var(--reader-tabs-strong, var(--color-grey-900));
        --context-menu-item-active-bg: var(--reader-tabs-hover-bg, var(--color-purple-50));
        --context-menu-item-active-separator: var(
            --reader-tabs-tab-hover-border,
            var(--color-purple-200)
        );
        --context-menu-item-active-text: var(--reader-tabs-strong, var(--color-grey-900));
    }

    :global(.darkmode) .context-menu {
        --context-menu-surface: var(--color-grey-900);
        --context-menu-border: var(--color-grey-700);
        --context-menu-text: var(--color-grey-50);
        --context-menu-item-bg: var(--color-grey-800);
        --context-menu-item-separator: var(--color-grey-700);
        --context-menu-item-text: var(--color-purple-100);
        --context-menu-item-icon: var(--color-grey-300);
        --context-menu-item-hover-bg: var(--color-grey-700);
        --context-menu-item-hover-separator: var(--color-grey-700);
        --context-menu-item-hover-text: var(--color-grey-0);
        --context-menu-item-active-bg: var(--color-grey-700);
        --context-menu-item-active-separator: var(--color-grey-700);
        --context-menu-item-active-text: var(--color-grey-0);
        --context-menu-danger-bg: var(--context-menu-item-bg);
        --context-menu-danger-separator: color-mix(
            in srgb,
            var(--danger) 34%,
            var(--context-menu-item-separator)
        );
        --context-menu-danger-hover-bg: color-mix(
            in srgb,
            var(--danger) 18%,
            var(--context-menu-item-bg)
        );
        --context-menu-danger-active-bg: color-mix(
            in srgb,
            var(--danger) 24%,
            var(--context-menu-item-bg)
        );
    }

    :global(.darkmode) .context-menu.reader-tabs-menu {
        --context-menu-surface: var(--reader-tabs-tab-bg, var(--color-grey-800));
        --context-menu-border: var(--reader-tabs-tab-border, var(--color-grey-700));
        --context-menu-text: var(--reader-tabs-strong, var(--color-grey-50));
        --context-menu-item-bg: var(--reader-tabs-tab-bg, var(--color-grey-800));
        --context-menu-item-separator: var(--reader-tabs-tab-border, var(--color-grey-700));
        --context-menu-item-text: var(--reader-tabs-strong, var(--color-grey-50));
        --context-menu-item-icon: var(--reader-tabs-muted, var(--color-grey-300));
        --context-menu-item-hover-bg: var(--reader-tabs-hover-bg, var(--color-grey-800));
        --context-menu-item-hover-separator: var(
            --reader-tabs-tab-hover-border,
            var(--color-grey-600)
        );
        --context-menu-item-hover-text: var(--reader-tabs-strong, var(--color-grey-50));
        --context-menu-item-active-bg: var(--reader-tabs-hover-bg, var(--color-grey-800));
        --context-menu-item-active-separator: var(
            --reader-tabs-tab-hover-border,
            var(--color-grey-600)
        );
        --context-menu-item-active-text: var(--reader-tabs-strong, var(--color-grey-50));
    }

    .absolute-menu {
        position: absolute;
    }

    .fixed-menu {
        position: fixed;
        z-index: 40;
    }

    [data-placement="absolute"][data-position="tl"] {
        right: 0;
        bottom: 0;
    }

    [data-placement="absolute"][data-position="bl"] {
        right: 0;
        top: 0;
    }

    [data-placement="absolute"][data-position="tr"] {
        left: 0;
        bottom: 0;
    }

    [data-placement="absolute"][data-position="br"] {
        left: 0;
        top: 0;
    }
</style>
